/**
 * `<OwnerUserNameSettings>` — the user's display name.
 *
 * Phase 8 of the 2026-05-18 extraction-remediation plan.
 *
 * The MCP performance evaluation found that "I" / "me" / "my action items" in chat
 * queries had no good way to resolve to a real human name — the system fell back to
 * "the user" in LLM prompts, which led to fuzzy / generic responses. This panel lets
 * the user set their name once; the backend threads it through chat-system prompts and
 * action-item assignee resolution.
 *
 * Distinct from `<OwnerEntitySettings>`:
 *   - `OwnerEntitySettings` sets the `entities.id` that `assignee="me"` resolves to —
 *     a CORPUS-level fact (which extracted person entity is "you").
 *   - `OwnerUserNameSettings` sets a free-text display string for LLM-prompt
 *     personalization — a UI-level convenience that doesn't touch the entity table.
 *
 * Storage: `app_settings['owner_user_name']` (see `aletheia/db/schema_meta.py`).
 */

import { useState } from 'react';
import { Badge, Button, Input } from '@/components/ui';
import { SettingsCard } from './SettingsCard';

interface OwnerUserNameSettingsProps {
  /** The currently-stored name, or `null` if not set. */
  userName: string | null;
  /** Called when the user clicks Save. Pass `null` to clear. */
  onSave: (userName: string | null) => void;
  /** True while a save is in flight (disables the button). */
  saving: boolean;
}

export function OwnerUserNameSettings({
  userName,
  onSave,
  saving,
}: OwnerUserNameSettingsProps): JSX.Element {
  const [draft, setDraft] = useState(userName ?? '');

  const trimmed = draft.trim();
  const current = userName ?? '';
  const dirty = trimmed !== current;

  return (
    <SettingsCard
      id="owner-user-name"
      title="Your name"
      desc={
        <>
          How the assistant addresses you in prompts and answers. Leave blank for
          anonymous.
        </>
      }
    >
      {/* U4: Tailwind-utility mix replaced by settings.css classes. */}
      <div className="settings-controls">
        <Input
          value={draft}
          placeholder="e.g. Chris"
          onChange={(e) => setDraft(e.target.value)}
          className="settings-grow"
          maxLength={64}
        />
        <Button
          size="sm"
          disabled={saving || !dirty}
          onClick={() => onSave(trimmed.length > 0 ? trimmed : null)}
        >
          Save
        </Button>
      </div>
      {userName !== null && userName.length > 0 && (
        <Badge variant="default">Current: {userName}</Badge>
      )}
    </SettingsCard>
  );
}
